import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

export interface ProjectDevice {
  id: string;
  type: 'smoke_detector' | 'heat_detector' | 'pull_station' | 'horn_strobe' | 'speaker' | 'module';
  manufacturer: string;
  model: string;
  location: string;
  quantity: number;
  notes?: string;
}

export interface ProjectCalculation {
  id: string;
  type: 'battery' | 'voltage_drop' | 'speaker_coverage' | 'detector_spacing';
  name: string;
  inputs: Record<string, string | number>;
  results: Record<string, string | number>;
  timestamp: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  clientName: string;
  projectAddress: string;
  city: string;
  state: string;
  zipCode: string;
  buildingType: string;
  squareFootage: number;
  numberOfFloors: number;
  systemType: string;
  devices: ProjectDevice[];
  calculations: ProjectCalculation[];
  notes: string;
  status: 'draft' | 'in_progress' | 'review' | 'completed';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  createProject: (projectData: Partial<Project>) => Promise<Project>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  loadProject: (projectId: string) => Promise<void>;
  saveCurrentProject: () => Promise<void>;
  addDevice: (device: Omit<ProjectDevice, 'id'>) => void;
  removeDevice: (deviceId: string) => void;
  updateDevice: (deviceId: string, updates: Partial<ProjectDevice>) => void;
  addCalculation: (calculation: Omit<ProjectCalculation, 'id' | 'timestamp'>) => void;
  removeCalculation: (calculationId: string) => void;
  clearCurrentProject: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load user's projects on mount
  useEffect(() => {
    if (user) {
      loadUserProjects();
    } else {
      setProjects([]);
      setCurrentProject(null);
    }
  }, [user]);

  const loadUserProjects = async () => {
    setIsLoading(true);
    try {
      // In production, fetch from API
      // const response = await fetch(`/api/projects?userId=${user.id}`);
      // const data = await response.json();
      
      // Simulate API call - load from localStorage
      const storedProjects = localStorage.getItem(`projects_${user?.id}`);
      if (storedProjects) {
        setProjects(JSON.parse(storedProjects));
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProjectsToStorage = (updatedProjects: Project[]) => {
    if (user) {
      localStorage.setItem(`projects_${user.id}`, JSON.stringify(updatedProjects));
      setProjects(updatedProjects);
    }
  };

  const createProject = async (projectData: Partial<Project>): Promise<Project> => {
    if (!user) throw new Error('User must be logged in to create projects');

    setIsLoading(true);
    try {
      const newProject: Project = {
        id: `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: projectData.name || 'Untitled Project',
        description: projectData.description || '',
        clientName: projectData.clientName || '',
        projectAddress: projectData.projectAddress || '',
        city: projectData.city || '',
        state: projectData.state || 'CA',
        zipCode: projectData.zipCode || '',
        buildingType: projectData.buildingType || 'commercial',
        squareFootage: projectData.squareFootage || 0,
        numberOfFloors: projectData.numberOfFloors || 1,
        systemType: projectData.systemType || 'addressable',
        devices: [],
        calculations: [],
        notes: projectData.notes || '',
        status: 'draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: user.id
      };

      // In production, save to API
      // const response = await fetch('/api/projects', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(newProject)
      // });
      // const savedProject = await response.json();

      const updatedProjects = [...projects, newProject];
      saveProjectsToStorage(updatedProjects);
      setCurrentProject(newProject);

      return newProject;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProject = async (projectId: string, updates: Partial<Project>) => {
    setIsLoading(true);
    try {
      const updatedProjects = projects.map(p =>
        p.id === projectId
          ? { ...p, ...updates, updatedAt: new Date().toISOString() }
          : p
      );

      // In production, update via API
      // await fetch(`/api/projects/${projectId}`, {
      //   method: 'PATCH',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(updates)
      // });

      saveProjectsToStorage(updatedProjects);

      if (currentProject?.id === projectId) {
        setCurrentProject({ ...currentProject, ...updates, updatedAt: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProject = async (projectId: string) => {
    setIsLoading(true);
    try {
      // In production, delete via API
      // await fetch(`/api/projects/${projectId}`, { method: 'DELETE' });

      const updatedProjects = projects.filter(p => p.id !== projectId);
      saveProjectsToStorage(updatedProjects);

      if (currentProject?.id === projectId) {
        setCurrentProject(null);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loadProject = async (projectId: string) => {
    setIsLoading(true);
    try {
      // In production, fetch from API
      // const response = await fetch(`/api/projects/${projectId}`);
      // const project = await response.json();

      const project = projects.find(p => p.id === projectId);
      if (project) {
        setCurrentProject(project);
      } else {
        throw new Error('Project not found');
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const saveCurrentProject = async () => {
    if (!currentProject) return;
    await updateProject(currentProject.id, currentProject);
  };

  const addDevice = (device: Omit<ProjectDevice, 'id'>) => {
    if (!currentProject) return;

    const newDevice: ProjectDevice = {
      ...device,
      id: `dev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    const updatedProject = {
      ...currentProject,
      devices: [...currentProject.devices, newDevice],
      updatedAt: new Date().toISOString()
    };

    setCurrentProject(updatedProject);
  };

  const removeDevice = (deviceId: string) => {
    if (!currentProject) return;

    const updatedProject = {
      ...currentProject,
      devices: currentProject.devices.filter(d => d.id !== deviceId),
      updatedAt: new Date().toISOString()
    };

    setCurrentProject(updatedProject);
  };

  const updateDevice = (deviceId: string, updates: Partial<ProjectDevice>) => {
    if (!currentProject) return;

    const updatedProject = {
      ...currentProject,
      devices: currentProject.devices.map(d =>
        d.id === deviceId ? { ...d, ...updates } : d
      ),
      updatedAt: new Date().toISOString()
    };

    setCurrentProject(updatedProject);
  };

  const addCalculation = (calculation: Omit<ProjectCalculation, 'id' | 'timestamp'>) => {
    if (!currentProject) return;

    const newCalculation: ProjectCalculation = {
      ...calculation,
      id: `calc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };

    const updatedProject = {
      ...currentProject,
      calculations: [...currentProject.calculations, newCalculation],
      updatedAt: new Date().toISOString()
    };

    setCurrentProject(updatedProject);
  };

  const removeCalculation = (calculationId: string) => {
    if (!currentProject) return;

    const updatedProject = {
      ...currentProject,
      calculations: currentProject.calculations.filter(c => c.id !== calculationId),
      updatedAt: new Date().toISOString()
    };

    setCurrentProject(updatedProject);
  };

  const clearCurrentProject = () => {
    setCurrentProject(null);
  };

  const value = {
    projects,
    currentProject,
    isLoading,
    createProject,
    updateProject,
    deleteProject,
    loadProject,
    saveCurrentProject,
    addDevice,
    removeDevice,
    updateDevice,
    addCalculation,
    removeCalculation,
    clearCurrentProject
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProjects() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
}
